<template>
  <main class="container">
    <section>
      <h1 class="welcome-msg">Welcome, {{ userName && userName.split(' ')[0] }}!</h1>
      <HomePageUpdateWrapper />
      <h2 class="section-title">Instructional Design Tools</h2>
    </section>
    <section class="card-container">
      <InstructionalDesignCard
        accent-color="#733a95"
        hover-color="#4E1470"
        :img-src="homepageAssets.presentLevelsImg"
        :header="getPLHeader()"
        :card-description="`Find baseline assessments and write meaningful, data-driven present levels with strengths, needs, and ${effectsVisibility ? 'effect' : 'impact'} statements.`"
        :btn="{text: 'Present Levels Wizard', link: 'PresentLevelsNewWizard', query: {ref: 'home'}}"
        :other-links="[
          {text: 'Browse present levels', link: 'BrowsePresentLevelsPage', query: {ref: 'home'}},
          {text: 'Compose impact of disability', link: 'ImpactOfDisability', query: {ref: 'home'}, hideForNoIod: true},
          {text: 'Browse assessments', link: 'BrowseAssessments', query: {ref: 'home'}},
        ]"
      >
        <template v-if="iodVisibility" #header-button>
          <div class="pl-beta-container">
            <div
              class="updates-info"
              tabindex="0">
              <span class="updates-number">3</span>
              <span class="updates-text">Updates</span>
              <span class="material-icons-outlined info-icon">info</span>
            </div>
            <popover
              :auto-placement="false"
              trigger="hover-focus"
              target=".updates-info"
              custom-class="popover-as-tooltip"
              placement="top">
              <template slot="popover">
                <div class="popover-content">
                  <strong>Updates to the New Present Levels Wizard (Beta)</strong><br>
                  • Aligned goals<br>
                  • 14 new subjects added<br>
                  • Aligned standards
                </div>
              </template>
            </popover>
            <a :href="presentlevelsBetaURL" class="pl-beta-link btn btn-primary-purple">
              Try NEW Version
            </a>
          </div>
        </template>
      </InstructionalDesignCard>
      <InstructionalDesignCard
        accent-color="#00769f"
        hover-color="#04506A"
        :img-src="homepageAssets.goalsImg"
        :header="{line1: 'Develop', line2: 'Goals & Assessments'}"
        card-description="Write measurable, standard-aligned goals based on student need, and find resources to monitor progress toward goals."
        :btn="{text: 'Learning Goal Wizard', link: 'BrowseGoalsWizardModal', query: {ref: 'home'}}"
        :other-links="[
          {text: 'Browse goals', link: 'BrowseGoalsPage', query: {ref: 'home'}},
          {text: 'View goal index', link: 'GoalIndexPage', query: {ref: 'home'}},
          {text: 'Browse assessments', link: 'BrowseAssessments', query: {ref: 'home'}},
        ]"
      />
      <InstructionalDesignCard
        accent-color="#136320"
        hover-color="#064612"
        :img-src="homepageAssets.strategiesImg"
        :header="{line1: 'Discover', line2: 'Strategies & Supports'}"
        card-description="Implement IEPs with research-based strategies and accommodations for delivering specially designed instruction."
        :btn="{text: 'Strategy Wizard', link: 'StrategyWizard', query: {ref: 'home'}}"
        :other-links="[{text: 'Browse strategies', link: 'BrowseStrategiesPage', query: {ref: 'home'}}, {text: 'View strategy index', link: 'StrategyIndex', query: {ref: 'home'}}]"
      />
      <ThreadsCardLink v-if="threadsVisibility" style="grid-column: span 3" />
    </section>
    <section class="content-and-training-container">
      <div class="your-content-container">
        <h2 class="section-title">Your Content</h2>
        <RecentLibrary />
        <HomePageRecentlyViewed />
      </div>
      <div class="training-center-container">
        <OnDemandTraining class="on-demand-training"/>
        <UpcomingWebinars class="upcoming-webinars"/>
        <RecentUpdates class="recent-updates"/>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters } from 'vuex'
import InstructionalDesignCard from '@/components/home/InstructionalDesignCard'
import ThreadsCardLink from '@/components/home/ThreadsCardLink'
import RecentLibrary from '@/components/home/RecentLibraryFolders'
import OnDemandTraining from '@/components/home/OnDemandTraining'
import UpcomingWebinars from '@/components/home/UpcomingWebinars'
import RecentUpdates from '@/components/home/RecentUpdates'
import HomePageRecentlyViewed from '@/components/home/HomePageRecentlyViewed'
import HomePageUpdateWrapper from '@/components/home/HomePageUpdateWrapper'
import { toolkitUrls } from '@/links'

export default {
  name: 'Home',
  components: {
      InstructionalDesignCard,
      ThreadsCardLink,
      RecentLibrary,
      UpcomingWebinars,
      OnDemandTraining,
      RecentUpdates,
      HomePageRecentlyViewed,
      HomePageUpdateWrapper
  },
  computed: {
      ...mapGetters('visibility', ['betaVisibility', 'iodVisibility', 'effectsVisibility', 'threadsVisibility']),
      ...mapGetters('user', ['userName']),
    presentlevelsBetaURL() {
      const url = new URL(this.presentlevels.v2)
      url.searchParams.set('ref', 'home')
      if (this.betaVisibility) {
        url.searchParams.set('beta', 'true')
      }
      return url
    }
  },
  data() {
      return {
          ...toolkitUrls,
      }
  },
  methods: {
    getPLHeader() {
      return this.effectsVisibility
        ? {line1: 'Identify Present Levels &', line2: 'Effect Statements'}
        : {line1: 'Identify', line2: 'Present Levels'}
    },

  },
  created() {
      this.$sendEvent('Viewed Signed In Landing Page', { vue: true, ref: this.$route.query.ref })
  }
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.container {
    font-family: 'IBM Plex Sans';
    font-size: 16px;
}
.welcome-msg, .section-title {
    color: @header-text-dark;
}
.welcome-msg {
    font-size: 2rem;
    margin-top: 0;
    font-weight: @font-weight-600;
}
.section-title {
    text-transform: uppercase;
    font-size: 1rem;
    font-weight: @font-weight-600;
}

.pl-beta-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: var(--spacing-md);
  min-height: 3.5rem;
  margin-top: -1rem;
  position: relative;
}

.pl-beta-link {
  &.btn-primary-purple {
    padding: var(--spacing-md) var(--spacing-xl);
    font-weight: var(--font-medium);
    border-radius: var(--radius-base);
    font-size: var(--text-md);
    white-space: nowrap;
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.10), 0px 4px 6px -1px rgba(0, 0, 0, 0.10);
    transition: all 0.2s;
    border: none;
    background-color: @plPurple;
    color: white;
    display: inline-block;
    text-align: center;

    &:hover {
      background-color: #4E1470;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px -2px rgba(0, 0, 0, 0.15), 0px 6px 10px -1px rgba(0, 0, 0, 0.15);
      color: white;
      text-decoration: none;
    }
  }
}

.updates-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-base);
  transition: background-color 0.2s;
  position: absolute;
  top: 0;
  right: 0;
  height: 1.5rem;

  .updates-number {
    font-size: 0.75rem;
    font-weight: var(--font-bold);
    color: @plPurple;
    line-height: 1;
  }

  .updates-text {
    font-size: 0.75rem;
    font-weight: var(--font-medium);
    color: @plPurple;
    line-height: 1;
  }

  .info-icon {
    font-size: 1.125rem;
    color: @plPurple;
    margin-left: 0.125rem;
  }
}

.pl-beta-link {
  margin-top: 1.5rem;
}

.popover-content {
  text-align: center;
  color: white;
}

// Instructional Design Cards
.card-container {
    margin: 0 auto 1.5rem auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px 20px;
    max-width:100%;

    @media (max-width: @screen-lg) {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    }

    @media (max-width: @screen-md) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
}

// Content below the instructional design cards.
.content-and-training-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2.5rem;

  @media (max-width: @screen-lg) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }

  @media (max-width: @screen-md) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
  }
}

.training-center-container {
    display: grid;
    grid-template-rows: min-content min-content min-content;

    @media (min-width: @screen-sm) and (max-width: @screen-lg) {
        grid-template-columns: 2fr 1fr;
        grid-template-rows: min-content min-content;
        grid-template-areas:
            "onDemandTraining ."
            "recentUpdates webinars";

        .on-demand-training {
            grid-area: onDemandTraining;
        }
        .recent-updates {
            grid-area: recentUpdates;
        }
        .upcoming-webinars {
            grid-area: webinars;
        }
    }

    @media (max-width: @screen-xs-max) {
      grid-template-rows: min-content auto auto;
      grid-template-columns: 1fr;
    }
}
</style>
